import Layout from "@/components/Layout";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { 
  Car, 
  Users, 
  Shield, 
  Clock,
  MapPin,
  Wifi,
  Star,
  CheckCircle,
  Plane,
  ArrowRight,
  Zap
} from "lucide-react";
import { useVehicleImages } from "@/hooks/useVehicleImages";
import transfersHeroBg from "@/assets/transfers-hero-bg.jpg";
import { Link } from "react-router-dom";

const Transfers = () => {
  const { vehicleImages, loading } = useVehicleImages();

  const transferTypes = [
    { title: "Airport Transfers", description: "Reliable pickup and drop-off service from/to all Dominican airports", icon: Plane, routes: ["Airport to Bavaro/Punta Cana Hotels", "Airport to Cap Cana Resorts", "Airport to Uvero Alto Hotels", "Airport to La Romana"] },
    { title: "Hotel Transfers", description: "Comfortable transportation between hotels and destinations", icon: MapPin, routes: ["Hotel to Hotel Transfers", "Hotel to Shopping Centers", "Hotel to Restaurants", "Hotel to Attractions"] },
    { title: "Private Transportation", description: "Dedicated vehicle and driver for your exclusive use", icon: Car, routes: ["Custom Itineraries", "Multiple Destinations", "Wait Time Included", "Flexible Scheduling"] },
    { title: "Group Transportation", description: "Specialized service for large groups and corporate events", icon: Users, routes: ["Wedding Groups", "Corporate Events", "Family Reunions", "Tour Groups"] }
  ];

  const features = [
    { icon: Shield, title: "Safety First", description: "All vehicles regularly inspected and fully insured" },
    { icon: Clock, title: "On-Time Service", description: "Punctual pickup and delivery with flight monitoring" },
    { icon: Users, title: "Professional Drivers", description: "Licensed, experienced drivers with local knowledge" },
    { icon: Wifi, title: "Modern Amenities", description: "Air conditioning, comfortable seating, and modern features" }
  ];

  const popularRoutes = [
    { route: "Airport ↔ Bavaro/Punta Cana", time: "45 minutes", distance: "25 km", price: "From $35" },
    { route: "Airport ↔ Cap Cana", time: "30 minutes", distance: "15 km", price: "From $40" },
    { route: "Airport ↔ Uvero Alto", time: "60 minutes", distance: "35 km", price: "From $45" },
    { route: "Airport ↔ La Romana", time: "90 minutes", distance: "60 km", price: "From $65" }
  ];

  return (
    <Layout>
      {/* Hero Section */}
      <section className="relative pt-32 pb-24 overflow-hidden">
        <div 
          className="absolute inset-0 bg-cover bg-center"
          style={{ backgroundImage: `url(${transfersHeroBg})` }}
        />
        <div className="absolute inset-0 bg-gradient-to-b from-primary/85 via-primary/70 to-primary/90" />
        <div className="absolute inset-0 pointer-events-none">
          <div className="absolute top-20 right-20 w-72 h-72 bg-secondary/15 rounded-full blur-3xl" />
        </div>
        
        <div className="container mx-auto px-4 relative z-10">
          <div className="max-w-4xl mx-auto text-center text-white">
            <Badge className="mb-6 bg-secondary/90 text-secondary-foreground border-0 text-sm px-6 py-2 rounded-full">
              <Plane className="h-4 w-4 mr-1 inline" /> Transfer Services
            </Badge>
            <h1 className="text-5xl md:text-7xl font-black mb-6 leading-[0.9]">
              Reliable <span className="text-secondary">Transportation</span>
              <br />in Punta Cana
            </h1>
            <p className="text-xl text-white/80 max-w-2xl mx-auto font-light">
              Professional transfer services for individuals, families, and groups. 
              Comfortable, safe, and always on time.
            </p>
          </div>
        </div>
      </section>

      {/* Vehicle Fleet */}
      <section className="py-24 bg-background">
        <div className="absolute top-0 left-0 w-full h-1 bg-gradient-to-r from-primary via-secondary to-accent" />
        <div className="container mx-auto px-4">
          <div className="text-center mb-16">
            <Badge className="mb-4 bg-primary/10 text-primary border-primary/20 rounded-full px-6 py-2">Our Fleet</Badge>
            <h2 className="text-4xl md:text-5xl font-bold mb-4">Choose Your <span className="text-secondary">Perfect Vehicle</span></h2>
            <p className="text-lg text-muted-foreground max-w-2xl mx-auto">
              From intimate transfers to large group transportation, we have the perfect vehicle for every need
            </p>
          </div>

          {loading ? (
            <div className="text-center py-12">
              <div className="animate-spin-slow w-10 h-10 border-4 border-primary border-t-transparent rounded-full mx-auto" />
            </div>
          ) : vehicleImages.length === 0 ? (
            <div className="text-center py-12">
              <p className="text-muted-foreground">No vehicles available at the moment.</p>
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
              {vehicleImages
                .sort((a, b) => a.min_passengers - b.min_passengers)
                .map((vehicle, index) => (
                <Card key={vehicle.id} className={`text-center hover-lift border-0 shadow-md relative overflow-hidden ${index === 1 ? 'ring-2 ring-secondary shadow-secondary' : ''}`}>
                  {index === 1 && (
                    <Badge className="absolute top-4 right-4 z-10 bg-secondary text-secondary-foreground text-xs rounded-full">
                      Most Popular
                    </Badge>
                  )}
                  <CardHeader>
                    <div 
                      className="w-full h-36 bg-cover bg-center rounded-xl mb-4 relative overflow-hidden"
                      style={{ backgroundImage: `url(${vehicle.image_url})` }}
                    >
                      <div className="absolute inset-0 bg-gradient-to-t from-black/30 to-transparent rounded-xl" />
                    </div>
                    <CardTitle className="text-lg">{vehicle.vehicle_name}</CardTitle>
                    <Badge className={`mt-2 ${vehicle.service_type === 'standard' ? 'bg-primary/10 text-primary border-primary/20' : 'bg-secondary/10 text-secondary border-secondary/20'} rounded-full`}>
                      {vehicle.service_type === 'standard' ? 'Standard Service' : 'VIP Service'}
                    </Badge>
                    <CardDescription className="mt-2 text-sm">
                      {vehicle.description || 'Professional transfer service'}
                    </CardDescription>
                  </CardHeader>
                  <CardContent>
                    <div className="space-y-2 mb-4">
                      <div className="flex items-center justify-between text-sm">
                        <span className="text-muted-foreground">Capacity:</span>
                        <span className="font-semibold">{vehicle.min_passengers}-{vehicle.max_passengers} passengers</span>
                      </div>
                    </div>
                    <div className="space-y-1.5 mb-6">
                      {vehicle.features && vehicle.features.length > 0 ? (
                        vehicle.features.map((feature, featureIndex) => (
                          <div key={featureIndex} className="flex items-center space-x-2 text-sm">
                            <CheckCircle className="h-3.5 w-3.5 text-primary flex-shrink-0" />
                            <span className="text-muted-foreground text-xs">{feature}</span>
                          </div>
                        ))
                      ) : (
                        <div className="flex items-center space-x-2 text-sm">
                          <CheckCircle className="h-3.5 w-3.5 text-primary flex-shrink-0" />
                          <span className="text-muted-foreground text-xs">Professional Service</span>
                        </div>
                      )}
                    </div>
                    <Button className={`w-full rounded-full ${index === 1 ? 'bg-secondary hover:bg-secondary/90 shadow-secondary' : 'bg-primary hover:bg-primary/90 shadow-primary'}`}>
                      Book Now
                    </Button>
                  </CardContent>
                </Card>
              ))}
            </div>
          )}
        </div>
      </section>

      {/* Transfer Types */}
      <section className="py-24 bg-muted/30">
        <div className="container mx-auto px-4">
          <div className="text-center mb-16">
            <Badge className="mb-4 bg-secondary/10 text-secondary border-secondary/20 rounded-full px-6 py-2">Service Types</Badge>
            <h2 className="text-4xl md:text-5xl font-bold mb-4">Comprehensive <span className="text-primary">Transfer Solutions</span></h2>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {transferTypes.map((type, index) => (
              <Card key={index} className="hover-lift border-0 shadow-md overflow-hidden">
                <div className="h-1 bg-gradient-to-r from-primary to-secondary" />
                <CardHeader>
                  <div className="flex items-center space-x-4 mb-2">
                    <div className="w-12 h-12 bg-gradient-to-br from-primary to-accent rounded-2xl flex items-center justify-center shadow-lg">
                      <type.icon className="h-6 w-6 text-white" />
                    </div>
                    <div>
                      <CardTitle className="text-lg">{type.title}</CardTitle>
                      <CardDescription className="text-sm">{type.description}</CardDescription>
                    </div>
                  </div>
                </CardHeader>
                <CardContent>
                  <div className="grid grid-cols-2 gap-2">
                    {type.routes.map((route, routeIndex) => (
                      <div key={routeIndex} className="flex items-center space-x-2 text-sm">
                        <CheckCircle className="h-3.5 w-3.5 text-secondary flex-shrink-0" />
                        <span className="text-muted-foreground text-xs">{route}</span>
                      </div>
                    ))}
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Popular Routes */}
      <section className="py-24 bg-background">
        <div className="container mx-auto px-4">
          <div className="text-center mb-16">
            <Badge className="mb-4 bg-primary/10 text-primary border-primary/20 rounded-full px-6 py-2">Popular Routes</Badge>
            <h2 className="text-4xl md:text-5xl font-bold mb-4">Most Requested <span className="text-secondary">Routes</span></h2>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            {popularRoutes.map((route, index) => (
              <Card key={index} className="text-center hover-lift border-0 shadow-md overflow-hidden">
                <div className="h-1 bg-gradient-to-r from-secondary to-tropical" />
                <CardHeader>
                  <CardTitle className="text-sm font-bold">{route.route}</CardTitle>
                  <div className="text-2xl font-black text-secondary">{route.price}</div>
                </CardHeader>
                <CardContent>
                  <div className="space-y-2">
                    <div className="flex items-center justify-between text-sm">
                      <span className="text-muted-foreground">Duration:</span>
                      <span className="font-medium">{route.time}</span>
                    </div>
                    <div className="flex items-center justify-between text-sm">
                      <span className="text-muted-foreground">Distance:</span>
                      <span className="font-medium">{route.distance}</span>
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Features */}
      <section className="py-24 bg-muted/30">
        <div className="container mx-auto px-4">
          <div className="text-center mb-16">
            <Badge className="mb-4 bg-accent/10 text-accent border-accent/20 rounded-full px-6 py-2">Why Choose Our Transfers</Badge>
            <h2 className="text-4xl md:text-5xl font-bold mb-4">Premium <span className="text-secondary">Service Standards</span></h2>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            {features.map((feature, index) => (
              <Card key={index} className="text-center hover-lift border-0 shadow-md">
                <CardHeader>
                  <div className="mx-auto w-14 h-14 bg-gradient-to-br from-primary to-accent rounded-2xl flex items-center justify-center mb-4 shadow-lg">
                    <feature.icon className="h-7 w-7 text-white" />
                  </div>
                  <CardTitle className="text-lg">{feature.title}</CardTitle>
                </CardHeader>
                <CardContent>
                  <CardDescription className="text-sm">{feature.description}</CardDescription>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Booking CTA */}
      <section className="py-24 bg-gradient-to-br from-primary via-primary to-accent text-white relative overflow-hidden">
        <div className="absolute inset-0 pointer-events-none">
          <div className="absolute top-10 left-10 w-40 h-40 bg-secondary/20 rounded-full blur-3xl" />
          <div className="absolute bottom-10 right-10 w-60 h-60 bg-white/5 rounded-full blur-3xl" />
        </div>
        
        <div className="container mx-auto px-4 relative z-10">
          <div className="max-w-3xl mx-auto text-center">
            <Zap className="h-12 w-12 mx-auto mb-6 text-secondary animate-float" />
            <h2 className="text-4xl md:text-5xl font-black mb-6">
              Book Your Transfer Today
            </h2>
            <p className="text-xl text-white/80 mb-8 font-light">
              Secure, comfortable, and reliable transportation is just a booking away.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Link to="/">
                <Button size="lg" className="bg-secondary hover:bg-secondary/90 text-secondary-foreground text-lg px-10 py-6 h-auto rounded-full font-bold shadow-secondary group">
                  Get Instant Quote
                  <ArrowRight className="ml-2 h-5 w-5 group-hover:translate-x-1 transition-transform" />
                </Button>
              </Link>
            </div>
          </div>
        </div>
      </section>
    </Layout>
  );
};

export default Transfers;
